// Pattern: docs/design-patterns/frontend/component-patterns.md (layout component)
// Pattern: docs/design-patterns/frontend/accessibility.md (ARIA landmarks, focus management)
// Pattern: docs/design-patterns/frontend/responsive-design.md (mobile collapse)

import { type JSX, useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import { SettingsSidebar } from './SettingsSidebar';

const SIDEBAR_WIDTH = 200;

export function SettingsLayout(): JSX.Element {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  useEffect(() => {
    if (!isSidebarOpen) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeSidebar();
    };
    document.addEventListener('keydown', handleKeyDown);
    return (): void => document.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarOpen, closeSidebar]);

  return (
    <div className="flex min-h-full">
      {/* Mobile hamburger — fixed top-left on small screens */}
      <div className="md:hidden fixed top-20 left-4 z-30">
        <button
          type="button"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-expanded={isSidebarOpen}
          aria-controls="settings-sidebar"
          className="p-2 rounded-md bg-surface shadow hover:bg-surface text-muted hover:text-foreground transition-colors"
          aria-label={isSidebarOpen ? 'Close settings menu' : 'Open settings menu'}
        >
          {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="presentation">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeSidebar}
            onKeyDown={e => e.key === 'Escape' && closeSidebar()}
            role="presentation"
          />
          {/* Sidebar panel */}
          <div
            id="settings-sidebar"
            className="absolute inset-0 bg-background z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-default">
              <h2 className="text-lg font-semibold text-foreground">Settings</h2>
              <button
                type="button"
                onClick={closeSidebar}
                className="p-2 rounded-md hover:bg-surface text-muted hover:text-foreground transition-colors"
                aria-label="Close settings menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SettingsSidebar onNavigate={closeSidebar} />
          </div>
        </div>
      )}

      {/* Desktop sidebar — full-height left panel */}
      <aside
        style={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH }}
        className="hidden md:block shrink-0 sticky top-0 h-[calc(100vh-4rem)] overflow-y-auto border-r border-default"
      >
        <SettingsSidebar />
      </aside>

      {/* Content area */}
      <main className="flex-1 min-w-0 py-8 px-8 max-w-4xl">
        <Outlet />
      </main>
    </div>
  );
}
